import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './css/SignUp.css';
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom';
import bgImg from '../assets/img1.jpg';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Container, Typography, Button, Card, CardContent, Grid, useTheme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { registerUser, verifyIdentityCard } from '../features/auth/authAction';
import { alertActions } from '../features/auth/alertSlice';

const labels = {
    si: {
        title: 'ලියාපදිංචි වන්න',
        subtitle: 'ලියාපදිංචි වී අපගේ සේවාව භුක්ති විඳින්න',
        name: 'නාමය',
        email: 'ඔබගේ ඊමේල්',
        phone: 'දුරකථන අංකය',
        nic: 'හැඳුනුම්පත අංකය',
        password: 'මුරපදය',
        confirmPassword: 'මුරපදය තහවුරු කරන්න',
        submit: 'ලියාපදිංචි වන්න',
        registerWith: 'ලියාපදිංචි වන්න භාවිතා කරමින්',
        emailLabel: 'ඊමේල්',
        phoneLabel: 'දුරකථනය',
    },
    en: {
        title: 'Sign Up',
        subtitle: 'Register to enjoy our service',
        name: 'Name',
        email: 'Your Email',
        phone: 'Phone Number',
        nic: 'Identity Card Number',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        submit: 'Sign Up',
        registerWith: 'Register with',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
    }
};

const RegisterScreen = ({ language }) => {
    const { loading, loggedUserInfo, success } = useSelector((state) => state.auth);
    const nicResponse = useSelector((state) => state.user.idVerifyResult);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { register, handleSubmit } = useForm();
    const [submittedData, setSubmittedData] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [registrationType, setRegistrationType] = useState('email'); // 'email' or 'phone'
    const theme = useTheme();

    const t = labels[language] ?? labels.si;

    const [verificationRequired, setVerificationRequired] = useState(false);
    const [emailForResend, setEmailForResend] = useState('');

    useEffect(() => {
        if (nicResponse?.gender && submittedData) {
            const registerAction = async () => {
                const result = await dispatch(registerUser(submittedData));
                if (result.payload?.requires_verification) {
                    setVerificationRequired(true);
                    setEmailForResend(submittedData.email);
                } else if (result.payload?.access_token) {
                    // Registration successful and no verification required - navigate to service page
                    navigate(`/service/${result.payload.user_id}`);
                }
                setSubmittedData(null);
            };
            registerAction();
        }
    }, [nicResponse, dispatch, submittedData, navigate]);



    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const submitForm = (data) => {
        if (data.password !== data.confirmPassword) {
            dispatch(
                alertActions.createAlert({
                    message: "Password mismatch",
                    type: "warning"
                })
            );
            return;
        }

        // Validate phone number format if phone registration
        if (registrationType === 'phone') {
            const phoneRegex = /^[0-9]{10}$/;
            if (!phoneRegex.test(data.email)) {
                dispatch(
                    alertActions.createAlert({
                        message: "Please enter a valid 10-digit phone number",
                        type: "warning"
                    })
                );
                return;
            }
        } else {
            // Convert email to lowercase for email registration
            data.email = data.email.toLowerCase();
        }

        // Add registration type to data
        data.registration_type = registrationType;

        dispatch((verifyIdentityCard(data)));
        setSubmittedData(data);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'light'
                    ? 'linear-gradient(135deg, rgba(5, 74, 73, 0.05) 0%, rgba(212, 175, 55, 0.05) 100%)' // Emerald/Gold Tint
                    : '#121212',
                py: { xs: 4, md: 6 },
                px: { xs: 2, sm: 3 },
                mt: { xs: 6, md: 0 },
            }}
        >
            <Container maxWidth="lg">
                <Card
                    sx={{
                        borderRadius: 4,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        border: theme.palette.custom.sectionBorder,
                        overflow: 'hidden',
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: theme.palette.custom.secondaryGradient,
                        },
                    }}
                >
                    <Grid container>
                        <Grid item xs={12} md={7}>
                            <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 800,
                                            mb: 1.5,
                                            background: theme.palette.custom.mainGradient,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                                        }}
                                    >
                                        {t.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                        {t.subtitle}
                                    </Typography>
                                </Box>

                                {verificationRequired ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#054A49' }}>
                                            Check Your Email
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 4 }}>
                                            We've sent a verification link to your email address. Please click the link to verify your account and complete your registration.
                                        </Typography>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => navigate('/login')}
                                            sx={{ borderRadius: 999, py: 1.5, background: theme.palette.custom.mainGradient, mb: 2 }}
                                        >
                                            Go to Login
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="text"
                                            onClick={async () => {
                                                try {
                                                    await axios.post(`${process.env.REACT_APP_API_URL}/api/resend-verification`, { email: emailForResend });
                                                    dispatch(alertActions.createAlert({ message: 'Verification email resent!', type: 'success' }));
                                                } catch (err) {
                                                    dispatch(alertActions.createAlert({ message: err.response?.data?.message || 'Failed to resend email.', type: 'warning' }));
                                                }
                                            }}
                                            sx={{ textTransform: 'none', color: 'primary.main' }}
                                        >
                                            Didn't receive an email? Resend
                                        </Button>
                                    </Box>
                                ) : (
                                    <form onSubmit={handleSubmit(submitForm)}>
                                        <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
                                            <InputLabel>{t.name}</InputLabel>
                                            <OutlinedInput
                                                label={t.name}
                                                {...register('name')}
                                                sx={{
                                                    borderRadius: 2,
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        {/* Registration Type Toggle */}
                                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                                            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 500 }}>
                                                {t.registerWith}
                                            </Typography>
                                            <ToggleButtonGroup
                                                value={registrationType}
                                                exclusive
                                                onChange={(e, newType) => {
                                                    if (newType !== null) {
                                                        setRegistrationType(newType);
                                                    }
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    '& .MuiToggleButton-root': {
                                                        px: 3,
                                                        py: 1,
                                                        borderRadius: '8px !important',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        '&.Mui-selected': {
                                                            background: theme.palette.custom.mainGradient,
                                                            color: 'white',
                                                            borderColor: 'primary.main',
                                                            '&:hover': {
                                                                background: theme.palette.custom.mainGradient,
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <ToggleButton value="email" sx={{ gap: 1 }}>
                                                    <EmailIcon fontSize="small" />
                                                    {t.emailLabel}
                                                </ToggleButton>
                                                <ToggleButton value="phone" sx={{ gap: 1 }}>
                                                    <PhoneIcon fontSize="small" />
                                                    {t.phoneLabel}
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>

                                        {/* Conditional Email or Phone Input */}
                                        <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
                                            <InputLabel>
                                                {registrationType === 'email' ? t.email : t.phone}
                                            </InputLabel>
                                            <OutlinedInput
                                                label={registrationType === 'email' ? t.email : t.phone}
                                                type={registrationType === 'email' ? 'email' : 'tel'}
                                                placeholder={registrationType === 'phone' ? '0771234567' : ''}
                                                {...register('email')}
                                                startAdornment={
                                                    registrationType === 'phone' ? (
                                                        <InputAdornment position="start">
                                                            <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                                +94
                                                            </Typography>
                                                        </InputAdornment>
                                                    ) : null
                                                }
                                                sx={{
                                                    borderRadius: 2,
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
                                            <InputLabel>{t.nic}</InputLabel>
                                            <OutlinedInput
                                                label={t.nic}
                                                {...register('nic')}
                                                sx={{
                                                    borderRadius: 2,
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
                                            <InputLabel>{t.password}</InputLabel>
                                            <OutlinedInput
                                                type={showPassword ? 'text' : 'password'}
                                                label={t.password}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                {...register('password')}
                                                sx={{
                                                    borderRadius: 2,
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl fullWidth sx={{ mb: 3 }} variant="outlined">
                                            <InputLabel>{t.confirmPassword}</InputLabel>
                                            <OutlinedInput
                                                type={showPassword ? 'text' : 'password'}
                                                label={t.confirmPassword}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                {...register('confirmPassword')}
                                                sx={{
                                                    borderRadius: 2,
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            disabled={loading}
                                            sx={{
                                                py: 1.5,
                                                borderRadius: 999,
                                                fontSize: '1.1rem',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                background: theme.palette.custom.mainGradient,
                                                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                                '&:hover': {
                                                    background: theme.palette.custom.mainGradient,
                                                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                                                    transform: 'translateY(-2px)',
                                                },
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            {t.submit}
                                        </Button>
                                    </form>
                                )}
                            </CardContent>
                        </Grid>
                        <Grid
                            item
                            xs={0}
                            md={5}
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                component="img"
                                src={bgImg}
                                alt="background"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Container>
        </Box>
    );
};

export default RegisterScreen;
